"use client";
import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import { useHomeLanguage } from "./HomeLanguageContext";

const moduleChips = [
  { label: "HRM & Payroll", className: "bg-sky-50 text-sky-700 border-sky-100" },
  { label: "Smart Attendance", className: "bg-cyan-50 text-cyan-700 border-cyan-100" },
  { label: "Leave & Reimbursement", className: "bg-blue-50 text-blue-700 border-blue-100" },
  { label: "Overtime & Visit", className: "bg-indigo-50 text-indigo-700 border-indigo-100" },
  { label: "Accounting", className: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  { label: "Invoice & Budgeting", className: "bg-lime-50 text-lime-700 border-lime-100" },
  { label: "CRM", className: "bg-teal-50 text-teal-700 border-teal-100" },
  { label: "Client & Leads", className: "bg-green-50 text-green-700 border-green-100" },
  { label: "Project Management", className: "bg-violet-50 text-violet-700 border-violet-100" },
  { label: "Task & Kanban", className: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100" },
  { label: "Timesheet", className: "bg-purple-50 text-purple-700 border-purple-100" },
  { label: "Penyimpanan", className: "bg-slate-50 text-slate-700 border-slate-100" },
  { label: "Kolabo Cloud", className: "bg-zinc-50 text-zinc-700 border-zinc-100" },
  { label: "Kolabo Meet", className: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  { label: "Kolabo Form", className: "bg-stone-50 text-stone-700 border-stone-100" },
  { label: "Kolabo Terminal", className: "bg-orange-50 text-orange-700 border-orange-100" },
  { label: "LMS", className: "bg-amber-50 text-amber-700 border-amber-100" },
  { label: "AI Assistant", className: "bg-rose-50 text-rose-700 border-rose-100" },
  { label: "AI Meeting Recap", className: "bg-pink-50 text-pink-700 border-pink-100" },
  { label: "Asset Management", className: "bg-neutral-50 text-neutral-700 border-neutral-100" },
  { label: "Reporting Dashboard", className: "bg-gray-50 text-gray-700 border-gray-100" },
  { label: "Recruitment", className: "bg-indigo-50 text-indigo-700 border-indigo-100" },
];

export default function ValueProposition() {
  const { lang } = useHomeLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  const businessSegments =
    lang === "id"
      ? ["UMKM & Startup", "Perusahaan Menengah", "300+ Perusahaan", "Multi-Cabang"]
      : ["SMEs & Startups", "Mid-size Companies", "300+ Companies", "Multi-Branch Teams"];

  const showcaseCards =
    lang === "id"
      ? [
          {
            title: "HRM & Payroll",
            desc: "Manajemen SDM lengkap",
            src: "/assets/optimized/kolabo-hrm-1-7bd06d649d.webp",
          },
          {
            title: "Accounting",
            desc: "Pembukuan otomatis",
            src: "/assets/optimized/acc1-fffec54277.webp",
          },
          {
            title: "CRM",
            desc: "Kelola pelanggan",
            src: "/assets/optimized/kolabo-crm-1-4dfd8d0633.webp",
          },
          {
            title: "Project Management",
            desc: "Kelola proyek & tim",
            src: "/assets/optimized/kolabo-webapp-mockup-6-816746d7b7.webp",
          },
        ]
      : [
          {
            title: "HRM & Payroll",
            desc: "Complete people management",
            src: "/assets/optimized/kolabo-hrm-1-7bd06d649d.webp",
          },
          {
            title: "Accounting",
            desc: "Automated bookkeeping",
            src: "/assets/optimized/acc1-fffec54277.webp",
          },
          {
            title: "CRM",
            desc: "Manage customers",
            src: "/assets/optimized/kolabo-crm-1-4dfd8d0633.webp",
          },
          {
            title: "Project Management",
            desc: "Manage projects and teams",
            src: "/assets/optimized/kolabo-webapp-mockup-6-816746d7b7.webp",
          },
        ];

  const bosVsErpPoints =
    lang === "id"
      ? [
          {
            title: "Bukan hanya back-office",
            desc: "ERP umumnya fokus pada pencatatan transaksi, sedangkan Kolabo mencakup operasional harian dari tim hingga eksekusi proses.",
          },
          {
            title: "Kolaborasi terintegrasi",
            desc: "Absensi, Kolabo Meet, Kolabo Cloud, project, task, jadwal, approval, dan komunikasi tim berjalan dalam satu alur kerja multi platform.",
          },
          {
            title: "Satu ritme bisnis",
            desc: "HRM, Accounting, CRM, dan Project Management terhubung real-time agar keputusan lebih cepat dan tindak lanjut lebih rapi.",
          },
        ]
      : [
          {
            title: "Beyond back-office",
            desc: "Traditional ERP mainly handles transactional records, while Kolabo covers day-to-day operations from team activities to execution.",
          },
          {
            title: "Integrated collaboration",
            desc: "Attendance, Kolabo Meet, Kolabo Cloud, projects, tasks, schedules, approvals, and team communication run in one connected workflow.",
          },
          {
            title: "One business rhythm",
            desc: "HRM, Accounting, CRM, and Project Management are connected in real time, helping teams move faster with cleaner follow-through.",
          },
        ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -5% 0px" }
    );
    sectionRef.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-24 lg:py-28 bg-white relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-10 right-0 w-[520px] h-[520px] bg-gradient-to-bl from-primary/8 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-20 left-0 w-[460px] h-[460px] bg-gradient-to-tr from-secondary/8 to-transparent rounded-full blur-3xl" />
        <div className="absolute inset-0 dot-pattern opacity-[0.08]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10 animate-on-scroll">
          <span className="section-label mb-6 inline-flex">
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot inline-block" />
            {lang === "id" ? "Mengapa Kolabo?" : "Why Kolabo?"}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[3.4rem] font-bold text-fg leading-[1.1] tracking-[-0.03em] mb-4 mt-4">
            {lang === "id" ? "Satu Platform, " : "One Platform, "}
            <span className="gradient-text">{lang === "id" ? "Semua Solusi" : "All Core Solutions"}</span>
          </h2>
          <p className="text-fg-muted text-lg max-w-3xl mx-auto leading-relaxed">
            {lang === "id" ? (
              <>
                Cukup satu langganan <span className="font-bold text-cta">Rp 500.000/bulan</span> untuk menyatukan HRM,
                Accounting, CRM, dan Project Management dalam satu alur kerja yang rapi.
                Operasional lebih cepat, biaya software lebih hemat, dan kontrol bisnis jauh lebih tenang.
              </>
            ) : (
              <>
                One subscription at <span className="font-bold text-cta">IDR 500k/month</span> to unify HRM, Accounting,
                CRM, and Project Management in one clean workflow.
                Faster operations, lower software cost, and better business control.
              </>
            )}
          </p>
          <p className="sr-only">
            {lang === "id"
              ? "Kolabo juga dirancang sebagai software HRM/HRIS, software absensi, aplikasi absensi, software payroll, software accounting, software project management, hingga software management perusahaan yang saling terhubung."
              : "Kolabo is also designed as an integrated HRM/HRIS platform, attendance software, payroll software, accounting software, project management software, and complete company management software."}
          </p>
          <div className="mt-6 text-left max-w-6xl mx-auto rounded-3xl border border-primary/20 bg-white/75 backdrop-blur-sm p-5 sm:p-6 shadow-card">
            <p className="text-fg text-[22px] sm:text-2xl font-heading font-bold tracking-tight">
              {lang === "id" ? "Kolabo bukan sekedar ERP tetapi " : "Kolabo is not just an ERP, but a "}
              <span className="gradient-text">Business Operating System</span>
            </p>
            <p className="mt-2 text-fg/80 text-sm sm:text-base leading-relaxed">
              {lang === "id"
                ? "Kolabo menyatukan kerja tim dan proses perusahaan dalam satu platform agar operasional harian lebih terukur, cepat, dan terintegrasi."
                : "Kolabo unifies team collaboration and company processes in one platform so daily operations become measurable, faster, and fully integrated."}
            </p>
            <p className="mt-2 text-fg/75 text-sm leading-relaxed">
              {lang === "id"
                ? "Data tersimpan aman dengan backup harian dan dukungan rollback saat dibutuhkan. Kolabo juga menerima request custom modul/fitur sesuai kebutuhan operasional perusahaan."
                : "Data is securely stored with daily backups and rollback support when needed. Kolabo also accepts custom feature/module requests based on your operational needs."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[12px] font-semibold text-emerald-700">
                {lang === "id"
                  ? "Data aman, backup harian, dan tersedia rollback"
                  : "Secure data, daily backups, and rollback support"}
              </span>
              <span className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[12px] font-semibold text-teal-700">
                {lang === "id"
                  ? "Menerima custom modul/fitur sesuai kebutuhan"
                  : "Custom feature/module requests are welcome"}
              </span>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {bosVsErpPoints.map((item) => (
                <div key={item.title} className="rounded-2xl border border-stone-200 bg-white px-4 py-3">
                  <p className="text-fg font-semibold text-[15px]">{item.title}</p>
                  <p className="mt-1.5 text-fg/70 text-[13px] leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full max-w-6xl mx-auto h-[3px] bg-gradient-to-r from-cyan-400 via-emerald-400 to-lime-400 rounded-full mt-8" />
        </div>

        <div className="grid lg:grid-cols-12 gap-5 animate-on-scroll">
          <div className="lg:col-span-8 space-y-4">
            <div className="rounded-3xl border border-stone-200 bg-gradient-to-br from-white to-brand-50/50 p-6 sm:p-7 shadow-card spotlight-card">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-brand flex items-center justify-center text-white shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 6h7v7H4zM13 6h7v7h-7zM4 15h7v7H4zM13 15h7v7h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-bold text-fg tracking-tight">
                    {lang === "id" ? "Ratusan Modul Lengkap, Plug & Play" : "Hundreds of Modules, Plug & Play"}
                  </h3>
                  <p className="text-fg-muted mt-2 leading-relaxed text-[15px]">
                    {lang === "id"
                      ? "Dirancang untuk semua divisi: Direktur, Management, HR, Finance, hingga Staff operasional. Dari UMKM sampai perusahaan dengan ribuan karyawan, implementasi cepat tanpa setup yang rumit."
                      : "Built for every division: Directors, Management, HR, Finance, and Staff. From SMEs to companies with thousands of employees, implementation stays fast and practical."}
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2.5">
                {moduleChips.map((chip) => (
                  <span
                    key={chip.label}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${chip.className}`}
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-card relative overflow-hidden">
                <div className="absolute -right-10 -bottom-10 w-28 h-28 rounded-full bg-primary/5" />
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7H15a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
                <h4 className="font-heading text-2xl font-bold text-fg tracking-tight">
                  {lang === "id" ? "Mahal Pakai Banyak Aplikasi" : "Too Costly with Many Apps"}
                </h4>
                <p className="text-fg-muted mt-2 leading-relaxed text-[15px]">
                  {lang === "id"
                    ? "Tanpa integrasi, biaya software mudah membesar dan data tercecer di banyak tools. Di Kolabo, semuanya menyatu dalam satu ekosistem agar pengeluaran lebih efisien dan laporan lebih rapi."
                    : "Without integration, software costs grow quickly and data gets scattered across tools. In Kolabo, everything is unified in one ecosystem for better efficiency and cleaner reporting."}
                </p>
              </div>

              <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-card relative overflow-hidden">
                <div className="absolute -right-10 -bottom-10 w-28 h-28 rounded-full bg-secondary/5" />
                <div className="w-10 h-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center mb-4">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h4 className="font-heading text-2xl font-bold text-fg tracking-tight">
                  {lang === "id" ? "Karyawan Kurang Disiplin" : "Team Discipline is Hard to Track"}
                </h4>
                <p className="text-fg-muted mt-2 leading-relaxed text-[15px]">
                  {lang === "id"
                    ? "Aktivitas tim, attendance, approval, sampai task terpantau real-time dalam satu dashboard. Tindak lanjut lebih cepat, disiplin meningkat, dan hasil kerja jadi lebih terukur."
                    : "Team activity, attendance, approvals, and tasks are monitored in real time in one dashboard. Faster follow-up, better discipline, and more measurable outcomes."}
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-3xl border border-primary/20 bg-gradient-to-br from-[#0F2940] via-[#12243A] to-[#0D1E31] p-6 shadow-card h-full">
              <div className="w-11 h-11 rounded-xl bg-emerald-400/20 text-emerald-300 flex items-center justify-center mb-4">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l8 4v6c0 5-3.5 9.7-8 10-4.5-.3-8-5-8-10V6l8-4z" />
                </svg>
              </div>
              <h3 className="font-heading text-2xl font-bold text-white tracking-tight">
                {lang === "id" ? "Cocok untuk Semua Lini Bisnis" : "Built for Every Business Line"}
              </h3>
              <p className="text-white/70 mt-2 leading-relaxed text-[15px]">
                {lang === "id"
                  ? "Dipakai oleh UMKM, perusahaan menengah, hingga enterprise multi-cabang. Fitur lengkap, aman, dan siap menopang operasional harian yang terus bertumbuh."
                  : "Used by SMEs, mid-size companies, and multi-branch enterprises. Feature-complete, secure, and ready to support growing day-to-day operations."}
              </p>

              <div className="mt-5 space-y-2.5">
                {businessSegments.map((segment) => (
                  <div
                    key={segment}
                    className="rounded-xl border border-white/25 text-white/90 px-4 py-2.5 text-sm font-medium bg-white/5 backdrop-blur-sm"
                  >
                    {segment}
                  </div>
                ))}
              </div>

              <div className="mt-5 relative overflow-hidden rounded-2xl border border-emerald-200/40 bg-[linear-gradient(120deg,#0d4a3b_0%,#186c56_44%,#22a779_100%)] text-white text-center px-4 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.24),0_14px_30px_rgba(7,42,33,0.36)]">
                <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.28),transparent_52%),radial-gradient(circle_at_bottom_left,rgba(194,255,232,0.2),transparent_56%)]" />
                <div className="relative z-10">
                  <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/85">
                    {lang === "id" ? "Mulai dari" : "Starts from"}
                  </p>
                  <p className="font-heading text-[46px] sm:text-[52px] font-extrabold leading-[0.95] mt-1 tracking-[-0.03em] drop-shadow-[0_4px_12px_rgba(0,0,0,0.24)]">
                    Rp <span className="text-[#e7ffe3]">500rb</span>
                  </p>
                  <p className="text-[12px] mt-1.5 font-medium text-white/90 tracking-[0.02em]">
                    {lang === "id" ? "per bulan • semua fitur inti" : "per month • all core features"}
                  </p>
                  <p className="text-[11px] mt-1 font-semibold text-[#dfffe7]">
                    {lang === "id" ? "Lebih hemat daripada pakai banyak aplikasi terpisah" : "More cost-effective than using many separate apps"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 animate-on-scroll">
          {showcaseCards.map((item) => (
            <div
              key={item.title}
              className="relative h-[170px] rounded-2xl overflow-hidden border border-white/30 shadow-card group"
            >
              <AppImage
                src={item.src}
                alt={item.title}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 1024px) 50vw, 25vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 via-bg-dark/30 to-transparent" />
              <div className="absolute left-3 bottom-3">
                <h4 className="font-heading text-white text-lg font-bold leading-tight">{item.title}</h4>
                <p className="text-white/75 text-xs mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-9 animate-on-scroll">
          <p className="text-fg-muted">
            {lang === "id" ? "Fokus pada pertumbuhan bisnis, bukan ribet operasional." : "Focus on business growth, not operational complexity."}
          </p>
          <a
            href="#daftar"
            className="inline-flex mt-3 btn-gradient text-white text-sm font-bold px-7 py-3 rounded-full shadow-brand items-center gap-2"
          >
            {lang === "id" ? "Coba Gratis, Tanpa Ribet!" : "Try Free, No Hassle!"}
          </a>
        </div>
      </div>
    </section>
  );
}
